import { BrowserProvider, Contract, formatUnits } from 'ethers';
import { API_URL } from '../config';
import { magic } from '../lib/magic';

// TPC token address from environment variable
const USDC_CONTRACT_ADDRESS = process.env.REACT_APP_USDC_ADDRESS || '0xe5BABE7Ac819760e82C358055eaEa60277437A66';

// TPC has 6 decimal places (not 18 like ETH)
const USDC_DECIMALS = 6;

// ERC-20 ABI for balanceOf function
const ERC20_ABI = [
  'function balanceOf(address owner) view returns (uint256)',
  'function decimals() view returns (uint8)',
  'function symbol() view returns (string)',
];

export interface WalletBalances {
  totalUSDC: number;        // Raw USDC in wallet
  lockedInBets: number;     // USDC locked in active bets
  portfolioBalance: number; // totalUSDC + lockedInBets  
  cashBalance: number;      // totalUSDC (available for new bets)
}

export class WalletService {
  private provider: BrowserProvider | null = null;
  private usdcContract: Contract | null = null;

  async initializeProvider(): Promise<void> {
    try {
      // Create provider using Magic's RPC provider (as per Magic docs)
      this.provider = new BrowserProvider(magic.rpcProvider as any);
      
      // Wait for network to be ready
      await this.provider.getNetwork();
      
      // Initialize USDC contract with provider (no signer needed for read operations)
      this.usdcContract = new Contract(
        USDC_CONTRACT_ADDRESS,
        ERC20_ABI,
        this.provider
      );
      
      console.log('Wallet service initialized for Base network');
    } catch (error) {
      console.error('Failed to initialize wallet provider:', error);
      throw error;
    }
  }

  async getUSDCBalance(walletAddress: string): Promise<number> {
    try {
      console.log('Calling balanceOf for wallet:', walletAddress);
      console.log('Using contract address:', USDC_CONTRACT_ADDRESS);
      
      // Try using signer approach as per Magic docs
      const provider = new BrowserProvider(magic.rpcProvider as any);
      const signer = await provider.getSigner();
      
      // Create contract with signer
      const contract = new Contract(USDC_CONTRACT_ADDRESS, ERC20_ABI, signer);
      
      const balance = await contract.balanceOf(walletAddress);
      console.log('Raw balance result:', balance);
      
      // Convert from Wei to USDC (6 decimal places)
      const balanceFormatted = formatUnits(balance, USDC_DECIMALS);
      console.log('Formatted balance:', balanceFormatted);
      
      return parseFloat(balanceFormatted);
    } catch (error) {
      console.error('Failed to fetch USDC balance:', error);
      console.error('Error details:', error);
      return 0;
    }
  }

  async getLockedInBets(userIssuer: string): Promise<number> {
    try {
      console.log('🔍 Fetching locked stakes for userIssuer:', userIssuer);
      const response = await fetch(`${API_URL}/user/locked-stakes?user_issuer=${encodeURIComponent(userIssuer)}`);
      if (!response.ok) {
        console.error('Failed to fetch locked stakes:', response.status);
        return 0;
      }
      const data = await response.json();
      console.log('🔍 Locked stakes response:', data);
      return data.locked_amount || 0;
    } catch (error) {
      console.error('Error fetching locked stakes:', error);
      return 0;
    }
  }

  async getWalletBalances(walletAddress: string, userIssuer?: string): Promise<WalletBalances> {
    try {
      console.log('🔍 getWalletBalances called with:', { walletAddress, userIssuer });
      const [totalUSDC, lockedInBets] = await Promise.all([
        this.getUSDCBalance(walletAddress),
        userIssuer ? this.getLockedInBets(userIssuer) : 0,
      ]);

      console.log('🔍 Balance calculation:', { totalUSDC, lockedInBets });
      const portfolioBalance = totalUSDC + lockedInBets;
      const cashBalance = totalUSDC; // Available for new bets

      return {
        totalUSDC,
        lockedInBets,
        portfolioBalance,
        cashBalance,
      };
    } catch (error) {
      console.error('Failed to get wallet balances:', error);
      // Return default values on error
      return {
        totalUSDC: 0,
        lockedInBets: 0,
        portfolioBalance: 0,
        cashBalance: 0,
      };
    }
  }

  async refreshBalances(walletAddress: string, userIssuer?: string): Promise<WalletBalances> {
    console.log('Refreshing wallet balances...');
    // Re-initialize provider to ensure fresh connection
    await this.initializeProvider();
    return this.getWalletBalances(walletAddress, userIssuer);
  }
}

// Export singleton instance
export const walletService = new WalletService();